import { ApiProperty } from '@nestjs/swagger';
import { Type } from 'class-transformer';
import { IsDateString, IsNumber, IsObject, ValidateNested } from 'class-validator';
import type { ApiServerEntityIDType } from '../../types';

/**
 * Represents a single date range for a deal.
 */
export class DealDateRange {
  @ApiProperty({
    description: 'End date of the deal (ISO format)',
    example: '2025-09-30T18:00:00.000Z',
  })
  @IsDateString()
  endedAt: string;

  @ApiProperty({
    description: 'Start date of the deal (ISO format)',
    example: '2025-09-01T09:00:00.000Z',
  })
  @IsDateString()
  startedAt: string;
}

/**
 * Schedule of deals grouped by nanny ID.
 */
export class DealConsumerSchedule {
  @ApiProperty({
    description: 'Mapping of nanny IDs to their scheduled date ranges',
    type: 'object',
    additionalProperties: { type: 'array', items: { $ref: '#/components/schemas/DealDateRange' } },
    example: {
      101: [{ startedAt: '2025-09-01T09:00:00.000Z', endedAt: '2025-09-03T18:00:00.000Z' }],
      202: [{ startedAt: '2025-09-10T09:00:00.000Z', endedAt: '2025-09-12T18:00:00.000Z' }],
    },
  })
  @IsObject()
  @ValidateNested({ each: true })
  @Type(() => DealDateRange)
  schedule: Record<number, DealDateRange[]>;
}

/**
 * Request DTO to fetch consumer's deal schedule for a given month.
 */
export class DealConsumerScheduleRequest {
  @ApiProperty({
    description: 'Client ID requesting the schedule',
    example: 123456,
  })
  @IsNumber()
  clientId: ApiServerEntityIDType;

  @ApiProperty({
    description: 'Any date within the target month (ISO format)',
    example: '2025-09-01T00:00:00.000Z',
  })
  @IsDateString()
  anyMonthDate: string;
}
